<template>
    <DashboardCard
        id="frontline-manager-dash-score-graph-card"
        :icon="faChartMixed"
        :title="questionTypeHeader"
        :response-count="totalResponses"
        :show-error="showError"
        :on-reload-click="onReloadClick"
    >
        <template #content>
            <div v-if="loading" id="score-graph-card-content">
                <div class="content--score--loading">
                    <Skeleton
                        height="55px"
                        width="120px"
                        border-radius="12px"
                    />
                    <Skeleton
                        height="35px"
                        width="120px"
                        border-radius="12px"
                    />
                </div>
                <div class="content--graph">
                    <Skeleton height="160px" border-radius="12px" />
                </div>
            </div>
            <div v-else id="score-graph-card-content">
                <div class="content--score">
                    <div data-testid="fm-dash-graph-score" class="score-value">
                        {{
                            formatScoreWithPercentage(
                                cxScore,
                                isFiveScore,
                                csatInPercentage,
                                metric,
                                decimals
                            )
                        }}
                    </div>
                    <div class="change">
                        <div
                            class="change__value"
                            :class="{
                                positive: npsChange > 0,
                                negative: npsChange < 0,
                            }"
                        >
                            <FontAwesomeIcon
                                v-if="npsChange > 0"
                                :icon="faArrowUp"
                                size="sm"
                            />
                            <FontAwesomeIcon
                                v-if="npsChange < 0"
                                :icon="faArrowDown"
                                size="sm"
                            />
                            {{
                                formatScoreWithPercentage(
                                    npsChange,
                                    isFiveScore,
                                    csatInPercentage,
                                    metric,
                                    decimals
                                )
                            }}
                        </div>
                        <div class="change__period">{{ periodLabel }}</div>
                    </div>
                </div>
                <div class="content--graph">
                    <div v-if="goal" class="goal">
                        <img :src="DashedGoalLine" alt="goal line" />
                        Goal {{ questionTypeTitle }} {{ goal }}
                    </div>
                    <div class="line-chart">
                        <LineChart
                            v-if="graphData"
                            :graph-data="graphData"
                            :metric="metric"
                            :metric-title="questionTypeTitle"
                            :is-five-score="isFiveScore"
                            :csat-in-percentage="csatInPercentage"
                            :decimals="decimals"
                        />
                    </div>
                </div>
            </div>
        </template>
    </DashboardCard>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { faChartMixed } from 'fontawesome/pro-regular-svg-icons'
import { faArrowDown, faArrowUp } from 'fontawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from 'fontawesome/vue-fontawesome'
import Skeleton from 'primevue/skeleton'
import DashedGoalLine from '@/assets/img/Icons/dashed-goal-line.svg'
import { GraphData } from '@/entities/frontlineManagerDash'
import DashboardCard from '@/components/FrontlineManagerDash/DashboardCard.vue'
import LineChart from '@/components/FrontlineManagerDash/LineChart.vue'
import { formatScoreWithPercentage } from '@/utils/number'

interface Props {
    isFiveScore: boolean
    csatInPercentage: boolean
    filterTimeLabel: string
    graphData: GraphData | undefined
    metric: string
    loading: boolean
    showError?: boolean
    onReloadClick?: Function
    graphSummary?: {
        total?: number
        nps?: number
        change?: number
    }
    decimals: {
        nps: number
        csat: number
        fivestar: number
        ces: number
    }
}
const props = defineProps<Props>()

const goal = computed(() => {
    return props.graphData?.target
})

const totalResponses = computed(() => {
    return props.graphSummary?.total ?? 0
})

const npsChange = computed(() => {
    const change = Number(props.graphSummary?.change ?? 0)
    return change || 0
})

const cxScore = computed(() => {
    return props.graphSummary?.nps ?? 'N/A'
})

const questionTypeTitle = computed(() => {
    if (!props || !props.metric) {
        return 'NPS'
    }
    if (props.metric === 'fivestar') {
        return 'Five Star'
    }
    return props.metric?.toUpperCase() ?? ''
})

const questionTypeHeader = computed(() => {
    return questionTypeTitle.value + ' Score'
})

const periodLabel = computed(() => {
    const timelabel = props.filterTimeLabel

    if (npsChange.value === 0 || !npsChange.value) {
        if (timelabel === 'today') {
            return 'No changes today'
        }
        return 'No change since ' + timelabel
    }

    if (timelabel === 'today') {
        return 'Today'
    } else {
        return 'vs last ' + timelabel
    }
})
</script>

<style lang="less" scoped>
@import '~@/styles/rain/variables.less';
@import '~@/styles/prime-vue/variables.less';
@import '~@/styles/prime-vue/colours.less';

#score-graph-card-content {
    display: flex;
    flex-direction: column;
    gap: @spacing-3xl;

    .content--score {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;

        .score-value {
            font-size: @fontSize-3xl;
            font-weight: @fontWeight-bold;
            line-height: @fontSize-3xl;
            letter-spacing: -1.2px;
            color: var(--p-gray-900);
        }

        .change {
            display: flex;
            gap: @spacing-xs;

            &__value {
                font-size: @fontSize-md;
                font-weight: @fontWeight-medium;
                line-height: @lineHeight-base;

                &.positive {
                    color: var(--p-success-600);
                }

                &.negative {
                    color: var(--p-error-600);
                }
            }

            &__period {
                font-size: @fontSize-sm;
                font-weight: @fontWeight-medium;
                line-height: @lineHeight-base;
                color: var(--p-gray-600);
            }
        }

        &--loading {
            display: flex;
            gap: @spacing-md;
            flex-direction: column;
            align-items: center;
        }
    }

    .content--graph {
        flex: 2;
        display: flex;
        flex-direction: column;
        align-items: center;

        .line-chart {
            width: 100%;
        }

        .goal {
            width: fit-content;
            display: flex;
            align-items: center;
            gap: @spacing-md;
            color: @text-secondary;
            font-size: @fontSize-sm;
            line-height: @lineHeight-sm;
            font-weight: @fontWeight-medium;
        }
    }
}

@media (min-width: @desktopBreakpoint) {
    #score-graph-card-content {
        flex-direction: row;
        gap: @spacing-3xl;

        .content--score {
            align-items: start;
            gap: 0;

            .score-value {
                line-height: @lineHeight-3xl;
            }

            .change {
                flex-direction: column;
                gap: 0;
            }

            &--loading {
                gap: @spacing-xl;
            }
        }
    }
}
</style>
